import { Link, useLocation } from "wouter";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { ShieldAlert, Globe, Menu, X, ArrowRight } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sheet, SheetClose, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

export function Layout({ children }: { children: React.ReactNode }) {
  const { t, language, setLanguage, isRTL } = useI18n();
  const [location] = useLocation();
  const navigationLinks = [
    { href: "/explore", label: t("Explore"), active: location.startsWith("/explore"), testId: "nav-explore" },
    { href: "/companies", label: t("Companies"), active: location.startsWith("/companies") || location.startsWith("/merchants"), testId: "nav-companies" },
    { href: "/deals", label: t("Deals"), active: location.startsWith("/deals"), testId: "nav-deals" },
    { href: "/about", label: t("About"), active: location === "/about" },
    { href: "/explore?type=product", label: t("Products"), active: location === "/explore?type=product", testId: "nav-products" },
    { href: "/explore?type=service", label: t("Services"), active: location === "/explore?type=service", testId: "nav-services" },
    { href: "/for-businesses", label: t("For Businesses"), active: location === "/for-businesses", testId: "nav-for-businesses" },
  ];

  return (
    <div className="min-h-[100dvh] flex flex-col bg-background selection:bg-primary/20">
      <header className="sticky top-0 z-50 w-full border-b border-border/60 bg-background/90 shadow-[0_4px_20px_-10px_rgba(6,42,96,0.1)] backdrop-blur-xl">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-3 group" data-testid="link-home">
            <div className="relative h-9 w-9 rounded-xl overflow-hidden shadow-md group-hover:shadow-lg transition-all duration-300 group-hover:-translate-y-0.5 border border-border/50">
              <img src="/brand/souq-path-concept.svg" alt="Souq Crypto" className="h-full w-full object-cover" />
            </div>
            <span dir="ltr" className="unicode-bidi-isolate font-extrabold text-xl tracking-tight text-[#062A60] dark:text-white hidden sm:block group-hover:text-primary transition-colors">
              Souq Crypto
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1 md:gap-2 text-sm font-medium">
            {navigationLinks.map(({ href, label, active, testId }) => (
              <Link key={href} href={href} className={`px-3 py-2 rounded-md transition-all duration-200 ${active ? "bg-primary/10 text-primary font-semibold" : "text-foreground/80 hover:bg-muted hover:text-foreground"}`} data-testid={testId}>
                {label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9 lg:hidden hover:bg-muted" data-testid="btn-mobile-menu">
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">{t("Open navigation")}</span>
                </Button>
              </SheetTrigger>
              <SheetContent side={isRTL ? "right" : "left"} className="w-[min(22rem,calc(100vw-2rem))] p-0 border-r-0">
                <div className="flex flex-col h-full bg-background">
                  <div className="p-6 border-b border-border/50 flex items-center justify-between">
                    <Link href="/" className="flex items-center gap-3" onClick={() => document.body.click()}>
                      <img src="/brand/souq-path-concept.svg" alt="Souq Crypto" className="h-8 w-8 rounded-lg shadow-sm" />
                      <span dir="ltr" className="font-extrabold text-lg tracking-tight text-[#062A60] dark:text-white">Souq Crypto</span>
                    </Link>
                    <SheetClose className="rounded-full p-2 hover:bg-muted transition-colors focus:outline-none focus:ring-2 focus:ring-primary">
                      <X className="h-5 w-5" />
                      <span className="sr-only">{t("Close navigation")}</span>
                    </SheetClose>
                  </div>
                  <div className="flex-1 overflow-y-auto py-6 px-4 flex flex-col gap-2">
                    <SheetTitle className="sr-only">{t("Navigation")}</SheetTitle>
                    {navigationLinks.map(({ href, label, active, testId }) => (
                      <SheetClose key={href} asChild>
                        <Link href={href} className={`rounded-xl px-4 py-3.5 text-base font-semibold transition-all ${active ? "bg-primary text-primary-foreground shadow-md" : "text-foreground/90 hover:bg-muted hover:translate-x-1"}`} data-testid={testId ? `mobile-${testId}` : undefined}>
                          {label}
                        </Link>
                      </SheetClose>
                    ))}
                  </div>
                  <div className="p-6 border-t border-border/50 bg-muted/20">
                    <SheetClose asChild>
                      <Link href="/list-your-business" className="flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-4 text-center font-bold text-white shadow-lg shadow-primary/25 hover:bg-primary/90 transition-all hover:-translate-y-0.5" data-testid="mobile-btn-list-business">
                        {t("List Your Business")} <ArrowRight className="h-4 w-4" />
                      </Link>
                    </SheetClose>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9 rounded-full border border-border/50 hover:bg-muted" data-testid="btn-lang-toggle">
                  <Globe className="h-4 w-4" />
                  <span className="sr-only">{t("Toggle language")}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-xl p-1 w-32 shadow-xl border-border/60">
                <DropdownMenuItem onClick={() => { setLanguage("en"); import("@/lib/analytics").then(m => m.trackLanguageChanged("en")).catch(() => {}); }} className={`rounded-lg cursor-pointer ${language === 'en' ? "bg-primary/10 text-primary font-bold" : "font-medium"}`} data-testid="menu-lang-en">
                  English
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { setLanguage("ar"); import("@/lib/analytics").then(m => m.trackLanguageChanged("ar")).catch(() => {}); }} className={`rounded-lg cursor-pointer ${language === 'ar' ? "bg-primary/10 text-primary font-bold" : "font-medium"}`} data-testid="menu-lang-ar">
                  العربية
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button asChild variant="default" size="sm" className="hidden sm:inline-flex rounded-full px-5 font-bold shadow-md shadow-primary/20 hover:shadow-lg hover:shadow-primary/30 transition-all hover:-translate-y-0.5">
              <Link href="/list-your-business" data-testid="btn-list-business">{t("List Your Business")}</Link>
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1 flex flex-col">
        {children}
      </main>

      <footer className="border-t border-border/60 bg-[#F7F9FC] dark:bg-card py-16 text-card-foreground">
        <div className="container mx-auto px-4 grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-8">
          <div className="md:col-span-2 space-y-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl overflow-hidden shadow-sm border border-border/50">
                <img src="/brand/souq-path-concept.svg" alt="" className="h-full w-full object-cover" />
              </div>
              <span dir="ltr" className="unicode-bidi-isolate font-extrabold text-2xl tracking-tight text-[#062A60] dark:text-white">Souq Crypto</span>
            </div>
            <p className="text-muted-foreground text-sm max-w-sm leading-relaxed">
              {t("Souq Crypto is an independent business directory. We do not exchange, receive, hold, transfer, or process cryptocurrency or customer funds. Customers and merchants arrange transactions independently and are responsible for confirming all payment terms.")}
            </p>
            <div className="flex items-start gap-3 text-xs font-medium text-muted-foreground bg-background border border-border/50 p-4 rounded-xl max-w-sm shadow-sm">
              <ShieldAlert className="h-5 w-5 text-accent shrink-0 mt-0.5" />
              <span className="leading-relaxed">{t("Disclaimer: Souq Crypto is an independent business directory.")}</span>
            </div>
          </div>

          <div className="space-y-6">
            <h4 className="font-bold text-sm tracking-widest uppercase text-foreground/50">{t("Platform")}</h4>
            <div className="flex flex-col gap-3 text-sm font-medium">
              <Link href="/explore" className="text-muted-foreground hover:text-primary transition-colors w-fit" data-testid="footer-link-explore">{t("Explore")}</Link>
              <Link href="/companies" className="text-muted-foreground hover:text-primary transition-colors w-fit" data-testid="footer-link-companies">{t("Companies")}</Link>
              <Link href="/deals" className="text-muted-foreground hover:text-primary transition-colors w-fit">{t("Deals")}</Link>
              <Link href="/categories" className="text-muted-foreground hover:text-primary transition-colors w-fit" data-testid="footer-link-categories">{t("Categories")}</Link>
              <Link href="/about" className="text-muted-foreground hover:text-primary transition-colors w-fit">{t("About")}</Link>
              <Link href="/faq" className="text-muted-foreground hover:text-primary transition-colors w-fit">{t("FAQ")}</Link>
              <Link href="/guide" className="text-muted-foreground hover:text-primary transition-colors w-fit">{t("Dubai guide")}</Link>
              <Link href="/for-businesses" className="text-muted-foreground hover:text-primary transition-colors w-fit" data-testid="footer-link-for-businesses">{t("For Businesses")}</Link>
              <Link href="/list-your-business" className="text-primary hover:text-primary/80 transition-colors w-fit" data-testid="footer-link-list-business">{t("List Your Business")}</Link>
            </div>
          </div>

          <div className="space-y-6">
            <h4 className="font-bold text-sm tracking-widest uppercase text-foreground/50">{t("Contact & Legal")}</h4>
            <div className="flex flex-col gap-3 text-sm font-medium">
              <a href="mailto:info@souq-crypto.com" dir="ltr" className="unicode-bidi-isolate text-muted-foreground hover:text-primary transition-colors w-fit" data-testid="footer-link-email">info@souq-crypto.com</a>
              <Link href="/privacy" className="text-muted-foreground hover:text-primary transition-colors w-fit" data-testid="footer-link-privacy">{t("Privacy Policy")}</Link>
              <button
                type="button"
                onClick={() => import("@/components/ConsentBanner").then(m => m.openConsentBanner()).catch(() => {})}
                className="text-muted-foreground hover:text-primary transition-colors w-fit text-start"
                data-testid="footer-link-cookie-preferences"
              >
                {language === "ar" ? "تفضيلات ملفات الارتباط" : "Cookie Preferences"}
              </button>
              <Link href="#" className="text-muted-foreground hover:text-primary transition-colors w-fit" data-testid="footer-link-terms">{t("Terms of Service")}</Link>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 mt-16 pt-8 border-t border-border/40 flex flex-col md:flex-row justify-between items-center gap-4 text-xs font-medium text-muted-foreground/80">
          <p><span dir="ltr" className="unicode-bidi-isolate">© {new Date().getFullYear()} Souq Crypto.</span> {t("All rights reserved.")}</p>
          <Link href="/admin" className="hover:text-foreground transition-colors" data-testid="footer-link-admin">{t("Admin Panel")}</Link>
        </div>
      </footer>
    </div>
  );
}
